import React, { useState, useEffect, useRef, Fragment } from 'react';
import {
  useListController,
  ListView,
  // TextField,
  EmailField,
  BooleanField,
  // Filter,
  // SelectInput,
  FunctionField,
  // NumberField,
  // DateField,
  useRefresh,
  useUnselectAll,
  useDataProvider,
  useNotify,
  downloadCSV,
  cacheDataProviderProxy,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import Papa from 'papaparse';
import { format } from 'date-fns';
import get from 'lodash/get';
import { Datagrid } from '../../components/common';
import { NewEvent } from '../../components/common/modals';
import {
  normalizePhone,
  validatePhoneOnSubmit,
} from '../../components/common/modals/modalForms';
import {
  AddIcon,
  EditIcon,
  DownloadIcon,
  UploadIcon,
  CustomButton,
} from '../../design';
import { useStyles } from './facility.styles';
import { IconButton } from '@material-ui/core';

const contactsExporter = (fields, facilityName) => {
  const fieldsForExport = fields.map(field => {
    const role =
      field.contact_role_other || get(field, 'contact_role.role', '');
    const ext = field.phone_ext ? `x${field.phone_ext}` : '';
    return {
      'First Name': field.first_name,
      'Last Name': field.last_name,
      Role: role,
      'Main Contact': field.is_main ? 'Yes' : 'No',
      'Office Phone': field.phone
        ? `${normalizePhone(field.phone)} ${ext}`.trim()
        : '',
      'Cell Phone': field.cell ? normalizePhone(field.cell) : '',
      Fax: field.fax ? normalizePhone(field.fax) : '',
      Email: field.email || '',
      'Gets NOMNC': field.gets_nomnc ? 'Yes' : 'No',
    };
  });
  jsonExport(fieldsForExport, (err, csv) => {
    downloadCSV(
      csv,
      `Facility_Contacts_${facilityName || ''}_${format(
        new Date(),
        'MM-dd-yyyy',
      )}`,
    );
  });
};

const ROLES_OTHER_ID = 13;

const parsePhoneAndExt = value => {
  if (!value) return { phone: null, phone_ext: null };
  const extMatch = value.match(/x(\d+)/i);
  const phone_ext = extMatch ? extMatch[1] : null;
  const phone = validatePhoneOnSubmit(value) || null;
  return { phone, phone_ext };
};

const MIN_PHONE_LENGTH = 10;

const validatePhone = value => {
  if (!value) return null;
  return value.length >= MIN_PHONE_LENGTH ? value : null;
};

const parseCsvRow = (row, rolesMap, rowIndex) => {
  const first_name = (row['First Name'] || '').trim();
  const last_name = (row['Last Name'] || '').trim();
  const roleName = (row['Role'] || '').trim();
  const matchedRole = rolesMap[roleName.toLowerCase()];
  const contact_role_id = matchedRole ? matchedRole.id : ROLES_OTHER_ID;
  const contact_role_other = !matchedRole && roleName ? roleName : null;
  const { phone: rawPhone, phone_ext } = parsePhoneAndExt(row['Office Phone']);
  const phone = validatePhone(rawPhone);
  const cell = validatePhone(validatePhoneOnSubmit(row['Cell Phone'])) || null;
  const fax = validatePhone(validatePhoneOnSubmit(row['Fax'])) || null;
  const email = (row['Email'] || '').trim() || null;
  const is_main = (row['Main Contact'] || '').trim().toLowerCase() === 'yes';
  const gets_nomnc = (row['Gets NOMNC'] || '').trim().toLowerCase() === 'yes';

  // Validate required fields (matching the manual form)
  const errors = [];
  if (!first_name) errors.push('first name');
  if (!last_name) errors.push('last name');
  if (!email) errors.push('email');
  if (!contact_role_id && !roleName) errors.push('role');
  if (errors.length > 0) {
    return {
      error: `Row ${rowIndex + 1} (${last_name ||
        first_name ||
        'unnamed'}): missing ${errors.join(', ')}`,
    };
  }

  // Warn on invalid phone lengths (non-blocking but stripped)
  const phoneWarnings = [];
  if (row['Office Phone'] && !phone) phoneWarnings.push('office phone');
  if (row['Cell Phone'] && !cell) phoneWarnings.push('cell phone');
  if (row['Fax'] && !fax) phoneWarnings.push('fax');

  return {
    data: {
      first_name,
      last_name,
      phone,
      phone_ext,
      cell,
      fax,
      email,
      contact_role_id,
      contact_role_other,
      is_main,
      gets_nomnc,
    },
    phoneWarnings,
  };
};

const ListActions = ({ record, data, ids }) => {
  const refresh = useRefresh();
  const dataProvider = cacheDataProviderProxy(useDataProvider());
  const notify = useNotify();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [eventOpen, setEventModal] = useState(false);
  const handleDownload = () => {
    if (!data || !ids) return;
    const fields = ids.map(id => data[id]).filter(Boolean);
    contactsExporter(fields, record?.name);
  };

  const handleUpload = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Reset so the same file can be re-selected
    event.target.value = '';
    setUploading(true);

    // Fetch contact roles for name → ID mapping
    let rolesMap = {};
    try {
      const { data: roles } = await dataProvider.getList('contact-roles/list', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
      });
      (roles || []).forEach(r => {
        rolesMap[(r.name || '').toLowerCase()] = r;
      });
    } catch (err) {
      notify('Failed to load contact roles', 'warning');
      setUploading(false);
      return;
    }

    // Parse CSV
    let parsed;
    try {
      parsed = await new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: results => resolve(results),
          error: err => reject(err),
        });
      });
    } catch (err) {
      notify('Failed to parse CSV file', 'warning');
      setUploading(false);
      return;
    }
    if (!parsed.data || parsed.data.length === 0) {
      notify('No contacts found in CSV', 'warning');
      setUploading(false);
      return;
    }

    // Validate and import rows
    let created = 0;
    const errors = [];
    for (let i = 0; i < parsed.data.length; i++) {
      const row = parsed.data[i];
      const result = parseCsvRow(row, rolesMap, i);
      if (result.error) {
        errors.push(result.error);
        continue;
      }
      const name = `${row['Last Name'] || ''}, ${row['First Name'] ||
        ''}`.trim();
      if (!result.data.first_name && !result.data.last_name) continue;
      try {
        await dataProvider.create('facility-contacts', {
          data: {
            ...result.data,
            facility_log_id: record?.facility_log_id,
          },
        });
        created++;
      } catch (err) {
        const reason =
          typeof err === 'string' ? err : err?.message || 'server error';
        errors.push(`Row ${i + 1} (${name || 'unnamed'}): ${reason}`);
      }
    }

    if (errors.length > 0) {
      notify(
        `Imported ${created} contact(s), ${errors.length} failed: ${errors.join(
          '; ',
        )}`,
        'warning',
      );
    } else {
      notify(`Imported ${created} contact(s)`, 'info');
    }
    if (created > 0) refresh();
    setUploading(false);
  };
  return (
    <Fragment>
      <div style={{ display: 'flex', marginLeft: 'auto', gap: 8 }}>
        <CustomButton
          Icon={AddIcon}
          color='#1061A0'
          backgroundColor='#EFF4FB'
          label='contact'
          type='button'
          variant='text'
          size='small'
          onClick={() => setEventModal(true)}
        />
        <CustomButton
          Icon={DownloadIcon}
          color='#1061A0'
          backgroundColor='#EFF4FB'
          label='Download'
          type='button'
          variant='text'
          size='small'
          onClick={handleDownload}
          disabled={!ids || ids.length === 0}
        />
        <CustomButton
          Icon={UploadIcon}
          color='#1061A0'
          backgroundColor='#EFF4FB'
          label='Upload'
          type='button'
          variant='text'
          size='small'
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        />
        <input
          ref={fileInputRef}
          type='file'
          accept='.csv'
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
      </div>
      {eventOpen && (
        <NewEvent
          open={eventOpen}
          handleClose={() => setEventModal(false)}
          form='facilityContacts'
          refresh={refresh}
          record={record}
        />
      )}
    </Fragment>
  );
};

export const FacilityContacts = ({
  id,
  isAdmin,
  isViewAdmin,
  isAdminAssistant,
  ...props
}) => {
  const classes = useStyles();
  const refresh = useRefresh();
  const [eventOpen, setEventModal] = useState(false);
  const [contactRecord, setContactRecord] = useState();
  const { record = {}, basePath, ...restProps } = props;

  useEffect(() => {
    // see comment on case_communication
    if (contactRecord) {
      setEventModal(true);
    }
  }, [contactRecord]);

  const unselectAll = useUnselectAll('facility-contacts');
  useEffect(() => {
    return () => unselectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModalClose = () => {
    setEventModal(false);
    setContactRecord(undefined);
  };

  const controllerProps = useListController({
    ...restProps, // Use restProps instead of props to exclude basePath
    resource: 'facility-contacts',
    filter: { facility_log_id: record?.facility_log_id },
    basePath, // Explicitly pass basePath if needed by useListController
  });

  // Extract basePath from controllerProps to prevent it from reaching ListView
  const {
    basePath: controllerBasePath,
    ...safeControllerProps
  } = controllerProps;

  return (
    <div className='show-list'>
      <ListView
        empty={false}
        {...safeControllerProps} // Use safeControllerProps without basePath
        bulkActionButtons={
          !isAdmin && !isAdminAssistant
            ? false // disable delete
            : undefined
        }
        exporter={false}
        actions={
          isAdmin || isAdminAssistant ? (
            <ListActions
              record={record}
              data={controllerProps.data}
              ids={controllerProps.ids}
            />
          ) : (
            <CustomButton
              Icon={DownloadIcon}
              color='#1061A0'
              backgroundColor='#EFF4FB'
              label='Download'
              type='button'
              variant='text'
              size='small'
              disabled={
                !controllerProps.ids || controllerProps.ids.length === 0
              }
              onClick={() => {
                const fields = controllerProps.ids
                  .map(id => controllerProps.data[id])
                  .filter(Boolean);
                contactsExporter(fields, record?.name);
              }}
            />
          )
        }
      >
        <Datagrid>
          <FunctionField
            source='first_name'
            label='Name'
            headerClassName={classes.listItemLabel}
            render={record => `${record.last_name}, ${record.first_name}`}
            sortBy='last_name'
          />
          <FunctionField
            source='contact_role.role'
            label='Role'
            headerClassName={classes.listItemLabel}
            render={record => {
              const { contact_role_other } = record;
              return contact_role_other || get(record, 'contact_role.role');
            }}
          />
          <FunctionField
            source='is_main'
            label='Main contact?'
            headerClassName={classes.listItemLabel}
            render={record => {
              const updatedRecord = {
                ...record,
                is_main: !!record.is_main,
              };
              return (
                <BooleanField
                  record={updatedRecord}
                  source='is_main'
                  headerClassName={classes.listItemLabel}
                  style={{
                    color: updatedRecord.is_main ? '#44C87A' : '#5C738E',
                  }}
                />
              );
            }}
          />
          <FunctionField
            source='phone'
            label='Office Phone'
            headerClassName={classes.listItemLabel}
            render={record => {
              const ext = record.phone_ext ? `x${record.phone_ext}` : '';
              return record.phone
                ? `${normalizePhone(record.phone)} ${ext}`
                : null;
            }}
          />
          <FunctionField
            source='cell'
            label='Cell Phone'
            headerClassName={classes.listItemLabel}
            render={record =>
              record.cell ? `${normalizePhone(record.cell)}` : null
            }
          />
          <FunctionField
            source='fax'
            label='Fax'
            headerClassName={classes.listItemLabel}
            render={record =>
              record.fax ? `${normalizePhone(record.fax)}` : null
            }
          />
          <EmailField
            source='email'
            label='Email'
            headerClassName={classes.listItemLabel}
          />
          <FunctionField
            source='gets_nomnc'
            label='Gets NOMNC?'
            headerClassName={classes.listItemLabel}
            render={record => {
              const updatedRecord = {
                ...record,
                gets_nomnc: !!record.gets_nomnc,
              };
              return (
                <BooleanField
                  record={updatedRecord}
                  source='gets_nomnc'
                  headerClassName={classes.listItemLabel}
                  style={{
                    color: updatedRecord.gets_nomnc ? '#44C87A' : '#5C738E',
                  }}
                />
              );
            }}
          />
          <FunctionField
            source='status'
            label=''
            sortable={false}
            render={record => {
              return isAdmin || isAdminAssistant ? (
                <IconButton
                  style={{ color: '#5C738E' }}
                  onClick={() => setContactRecord(record)}
                  size='small'
                >
                  <EditIcon />
                </IconButton>
              ) : null;
            }}
          />
        </Datagrid>
      </ListView>
      {eventOpen && (
        <NewEvent
          open={eventOpen}
          handleClose={handleModalClose}
          record={contactRecord}
          form='facilityContacts'
          isEdit
          refresh={refresh}
          payerFacilityId={id}
        />
      )}
    </div>
  );
};
